import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { CheckCircle2, Eye } from "lucide-react";
import { fmt } from "./utils";

export function ReplyModal({
  open,
  onOpenChange,
  replyText,
  setReplyText,
  replyPreview,
  setReplyPreview,
  replyAmountOverride,
  setReplyAmountOverride,
  replyReferenceOverride,
  setReplyReferenceOverride,
  busy,
  onPreview,
  onRePreview,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setReplyPreview(null); }}>
      <DialogContent className="max-w-lg" data-testid="log-reply-modal">
        <DialogHeader><DialogTitle>Log owner reply</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Paste the owner's reply. We'll pick out any UPI/UTR/reference + amount, split it across invoices FIFO (partial payments stay tracked), and preview the match before saving.</p>
          <textarea rows={5} value={replyText} onChange={(e) => { setReplyText(e.target.value); setReplyPreview(null); }}
                    placeholder="e.g. Paid ₹54,450 via UPI. Reference: UTR12345XYZ. — Anita"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                    data-testid="reply-body-input" />
          {replyPreview && (
            <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2 text-xs" data-testid="reply-preview">
              <div className="flex items-center justify-between">
                <span className="uppercase tracking-widest text-[10px] font-semibold text-primary">Parsed — tweak if needed</span>
                <span className="text-muted-foreground">Remaining after allocation: <span className="font-medium text-foreground">{fmt(replyPreview.remaining)}</span></span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-muted-foreground text-[10px] uppercase tracking-widest">Amount (₹)</span>
                  <Input type="number" min="0" step="0.01"
                         value={replyAmountOverride}
                         onChange={(e) => setReplyAmountOverride(e.target.value)}
                         className="mt-1 h-8 text-sm font-medium"
                         data-testid="preview-amount-input" />
                </div>
                <div>
                  <span className="text-muted-foreground text-[10px] uppercase tracking-widest">Reference</span>
                  <Input value={replyReferenceOverride}
                         onChange={(e) => setReplyReferenceOverride(e.target.value)}
                         className="mt-1 h-8 text-sm font-mono"
                         data-testid="preview-reference-input" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <button type="button" onClick={onRePreview}
                        disabled={busy || !replyAmountOverride}
                        className="text-[11px] text-primary hover:underline disabled:opacity-50"
                        data-testid="preview-recheck-btn">
                  Re-check allocation
                </button>
                {(Number(replyAmountOverride) !== Number(replyPreview.amount) || replyReferenceOverride !== replyPreview.reference) && (
                  <span className="text-[10px] uppercase tracking-widest text-amber-700">edited — re-check first</span>
                )}
              </div>
              {replyPreview.allocations.length === 0 ? (
                <div className="italic text-muted-foreground">No matching invoices — nothing will be written.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {replyPreview.allocations.map((a) => (
                    <li key={a.invoice_id} className="py-1.5 flex justify-between items-center gap-2" data-testid={`preview-alloc-${a.invoice_id}`}>
                      <span className="text-muted-foreground">Invoice ending {a.period_end}</span>
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{fmt(a.allocation)}</span>
                        {a.fully_paid ? (
                          <span className="text-[9px] uppercase tracking-widest rounded px-2 py-0.5 bg-primary/10 text-primary font-semibold">Full</span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-widest rounded px-2 py-0.5 bg-amber-100 text-amber-800 font-semibold">Partial · {fmt(a.outstanding - a.allocation)} left</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {!replyPreview ? (
            <Button type="button" onClick={onPreview} disabled={busy || !replyText.trim()} data-testid="reply-preview-btn">
              <Eye size={14} className="mr-2" />{busy ? "Parsing…" : "Preview match"}
            </Button>
          ) : (
            <Button type="button" onClick={onSubmit} disabled={busy || replyPreview.allocations.length === 0} data-testid="reply-submit-btn">
              <CheckCircle2 size={14} className="mr-2" />{busy ? "Saving…" : "Confirm & save"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
